import React from 'react';
import { useSelector } from 'react-redux';
import { groupBy } from 'lodash-es';
import classnames from 'classnames';
import { isTA as isTASelector, isSr as isSRSelector } from 'store/modules/auth';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { GuestAges, ICompany, LiveRateListItemWithGuestAges } from 'services/BackendApi';
import { getCurrencySymbol, formatPrice } from 'utils';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';
import Tooltip from 'pureUi/Tooltip';
import MousePositionProvider from 'pureUi/providers/MousePositionProvider';
import { RateMixingWarning } from 'components/RateMixingWarning';
import { BasketRateTypes, TCurrencyCode } from 'interfaces';
import ClockIcon from 'ui/Icons/clock.component.svg';
import { SvgIcon } from 'ui/SvgIcon';
import InfoCircleIcon from 'ui/Icons/tabler/info-circle.component.svg';
import { theme } from '../../../tailwind.config';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import {
  composeCancellationPolicyFromRoomExpenseInfo,
  isRefundable as isRefundableFn,
} from 'common-lib/cancellation-policy-composer';
import { RateInclusions } from './RateInclusions';
import { companyDataSelector } from 'store/modules/companyInfo';
import { ERefundabilityTagStatus, RefundabilityTag } from 'ui/RefundabilityTag/RefundabilityTag';
import { AvailabilityTag, EAvailabilityTagStatus } from 'ui/AvailabilityTag/AvailabilityTag';
import InfoIcon from 'ui/Icons/Info.component.svg';

export const normalizeArray = a => (a || []).sort();

export interface ILiveRateRequestData extends GuestAges {
  externalRateId: string;
  externalMealPlanCode: string;
  externalMealPlanDescription: string;
  amount: number;
}

const InfoTooltip = ({ content }: { content: React.ReactNode }) => {
  return (
    <MousePositionProvider
      className="acc-free-sale-rates-info-container flex items-center"
      render={mousePosition => {
        return (
          <>
            <SvgIcon
              IconComponent={InfoCircleIcon}
              className="acc-free-sale-rates-info w-4 h-4 ml-10px fill-gray-40 hover:fill-gray-80 active:fill-gray-80"
            />
            {mousePosition.isOver && (
              // @ts-ignore
              <Tooltip position={mousePosition}>{content}</Tooltip>
            )}
          </>
        );
      }}
    />
  );
};

const ExpiredCancellationPolicyTooltip = props => {
  const { cancellationPolicy } = props;
  return (
    <MousePositionProvider
      className="expired-cancellation-policy-tooltip-container"
      render={mousePosition => {
        return (
          <>
            <SvgIcon
              key={cancellationPolicy.cancellationPolicy + 'icon'}
              IconComponent={ClockIcon}
              width="22px"
              height="22px"
              className="p-1"
              defaultFill={theme.colors['gray-80']}
              hoverFill={theme.colors['gray-80']}
              activeFill={theme.colors['gray-80']}
            />
            {mousePosition.isOver && (
              // @ts-ignore
              <Tooltip position={mousePosition}>
                <span className="expired-cancellation-policy-tooltip px-2 py-1">
                  This cancellation deadline has passed.
                </span>
              </Tooltip>
            )}
          </>
        );
      }}
    />
  );
};

interface IGuestAgeSetInfoProps {
  adults: number;
  children: number;
}

const GuestAgeSetInfo: React.FC<IGuestAgeSetInfoProps> = ({ adults, children }) => (
  <span className="block text-brown-prime mb-10px">
    <span className="uppercase text-14px font-semibold mr-5px">{adults + children} Guest</span>
    <span>{adults} Adults</span>
    {children > 0 && (
      <span className="">
        {' '}
        / {children} {children > 1 ? 'Children' : 'Child'}
      </span>
    )}
  </span>
);

const EmailInstantBookMessage: React.FC = () => (
  <div className="flex items-center gap-2 p-2 bg-green-25 rounded-1px -mt-[10px] mb-[15px]">
    <SvgIcon IconComponent={InfoIcon} width="18px" height="18px" defaultFill={theme.colors['green-prime']} />
    <span className="text-13px leading-16px text-black font-hurmegeometric-sans">
      This is showing live rates and availability and is subject to change. To book please contact your account manager.
      If you would like to book live rates instantly email{' '}
      <a href="mailto:app@pure-escapes.com" className="underline text-black hover:text-gray-80">
        app@pure-escapes.com
      </a>
    </span>
  </div>
);

interface IInstantBookBlockProps {
  rates: LiveRateListItemWithGuestAges[];
  accommodationProduct: any;
  addRoom: (liveRate: ILiveRateRequestData) => void;
  basketRateType?: 'static' | 'live' | 'free_sale';
  isAddDisabled?: boolean;
  blockLabel: string;
  blockInfoTooltipContent: React.ReactNode;
  forBasketType: BasketRateTypes;
  currency: TCurrencyCode;
}

export const InstantBookBlock: React.FC<IInstantBookBlockProps> = (props: IInstantBookBlockProps) => {
  const { rates, basketRateType } = props;
  const { dynamicParameters } = useDynamicParameters();
  const { convert, convertCents, displayCurrencyCode, displayCurrencySymbol, isConverting } = useCurrencyConversion();

  // FIX OWA-5970 for the purpose of this component, admins qualify as SRs
  const isEffectivelySr = useSelector(isSRSelector) || useSelector(AuthSelectors.isAdmin);
  const isTA = useSelector(isTASelector);
  const taCompany = useSelector(companyDataSelector) as ICompany;

  const userCanBook = isTA || isEffectivelySr;
  const isLiveRatesEnabled = isTA
    ? dynamicParameters.ENABLE_V1_LIVE_RATES && taCompany?.enableInstantBooking
    : dynamicParameters.ENABLE_V1_LIVE_RATES || (dynamicParameters.ENABLE_V1_LIVE_RATES_SRS && isEffectivelySr);
  const ratesGroupedByAgeSet = groupBy(rates, fsr => {
    return `${fsr.guestAges.numberOfAdults}${fsr.guestAges.agesOfAllChildren?.join(',') || ''}`;
  });

  // Check if TA can't book instant rates (they're a TA but instant booking is not enabled for them)
  const isTaCantBookInstant = isTA && !isLiveRatesEnabled;

  // we're NOT allowed to add a rate of this type to the basket if:
  // - the basket rate type IS set, so we've got something in the basket
  // - but its not of the same type as the rate we're trying to add
  const isNotAllowedToBeAddedToBasket =
    props.basketRateType !== undefined && props.basketRateType !== props.forBasketType;

  return (
    <div className={`acc-free-sale-rates font-hurmegeometric-sans mt-0 mx-12px mb-15px p-15px rounded-1px bg-gray-7`}>
      <div className="flex items-center">
        <p className="acc-free-sale-rates-title not-italic font-bold text-sm leading-17px uppercase text-flint">
          {props.blockLabel}
        </p>
        {/* OWA-9847: Hiding tooltip for all users */}
        {/* {!isTaCantBookInstant && <InfoTooltip content={props.blockInfoTooltipContent} />} */}
      </div>
      <div className="acc-free-sale-rates-tagline mb-15px">
        <AvailabilityTag availabilityStatus={EAvailabilityTagStatus.AVAILABLE} className="acc-free-sale-rates-tag">
          AVAILABLE
        </AvailabilityTag>
      </div>

      {isNotAllowedToBeAddedToBasket && !isTaCantBookInstant && <RateMixingWarning isSr={isEffectivelySr} />}

      {isTaCantBookInstant && <EmailInstantBookMessage />}

      <div className="space-y-[25px]">
        {Object.keys(ratesGroupedByAgeSet).map(freeSaleRatesAgeSetKey => {
          const freeSaleRatesPerAgeSet = ratesGroupedByAgeSet[freeSaleRatesAgeSetKey];
          return (
            <div key={`${freeSaleRatesAgeSetKey}-${freeSaleRatesPerAgeSet[0].externalRateId}`}>
              <GuestAgeSetInfo
                adults={freeSaleRatesPerAgeSet[0].guestAges.numberOfAdults}
                children={freeSaleRatesPerAgeSet[0].guestAges.agesOfAllChildren?.length || 0}
              />
              <div className="space-y-20px">
                {freeSaleRatesPerAgeSet.map((rate, index) => {
                  const { numberOfAdults, agesOfAllChildren } = rate.guestAges;
                  const key = `${numberOfAdults}${agesOfAllChildren?.join(',')}${rate.externalRateId}${
                    rate.mealPlanCode
                  }`;

                  if (freeSaleRatesPerAgeSet.length === 0) {
                    return null;
                  }

                  const cancelInfo = {
                    expenseDeadlines: (rate.expensesDeadlines || []).map(ed => ({
                      ...ed,
                      amount: convert(ed.amount / 100),
                    })),
                    isLive: true,
                    isRefundable: null,
                    manualCancellationPolicy: [],
                  };

                  const isRefundable = isRefundableFn(cancelInfo);

                  const texts = composeCancellationPolicyFromRoomExpenseInfo(cancelInfo, {
                    currencySymbol: isConverting ? displayCurrencySymbol : getCurrencySymbol(props.currency),
                    appendLines: ['*at 00.00 time at destination'],
                  });

                  return (
                    <div className="acc-free-rate-item last:pb-0 last:border-b-0" key={key}>
                      <div
                        key={rate.externalRateId}
                        data-index={index}
                        className="live-rate-listing font-hurmegeometric-sans"
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-[8px]">
                          <div>
                            <span className="block uppercase text-brown-prime font-bold text-xs leading-14px">
                              {rate.mealPlanDescription}. TAX INCLUDED
                            </span>
                            {rate.features && <RateInclusions key={rate.externalRateId} features={rate.features} />}
                            <RefundabilityTag
                              refundabilityStatus={
                                isRefundable
                                  ? ERefundabilityTagStatus.REFUNDABLE
                                  : ERefundabilityTagStatus.NON_REFUNDABLE
                              }
                              className="acc-free-sale-rates-tag flex items-center"
                            />

                            {!!rate.expensesDeadlines?.length && (
                              <>
                                <div>
                                  {texts.map(cp => {
                                    return (
                                      <div key={cp} className="flex flex-row items-center">
                                        <span className={classnames('block mr-2')}>{cp}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            {!rate.expensesDeadlines?.length && (
                              <div>Please, confirm the cancellation policy of this rate with your sales contact</div>
                            )}
                          </div>
                          <div className="flex flex-row items-center">
                            <span className="mr-10px text-17px text-bistre">
                              {`${isConverting ? displayCurrencySymbol : getCurrencySymbol(props.currency)}${formatPrice(convert(rate.totalCents / 100))}`}
                            </span>
                            {isLiveRatesEnabled && userCanBook && !isTaCantBookInstant && (
                              <button
                                disabled={isNotAllowedToBeAddedToBasket || props.isAddDisabled}
                                onClick={() => {
                                  if (isNotAllowedToBeAddedToBasket || props.isAddDisabled) {
                                    return;
                                  }

                                  props.addRoom({
                                    ...props.accommodationProduct,
                                    liveRateData: {
                                      externalRateId: rate.externalRateId,
                                      externalMealPlanCode: rate.mealPlanCode,
                                      externalMealPlanDescription: rate.mealPlanDescription,
                                      amount: rate.totalCents,
                                      numberOfAdults: rate.guestAges.numberOfAdults,
                                      agesOfAllChildren: rate.guestAges.agesOfAllChildren ?? [],
                                    },
                                  });
                                }}
                                style={{
                                  width: '81px',
                                }}
                                className={classnames(
                                  `px-5px font-hurmegeometric-sans font-semibold border-none outline-none bg-brown-prime block h-35px text-white text-14px leading-17px uppercase`,
                                  {
                                    // not disabled
                                    'hover:bg-marine cursor-pointer': basketRateType !== BasketRateTypes.STATIC && !props.isAddDisabled,
                                    // disabled
                                    'opacity-50': basketRateType === BasketRateTypes.STATIC || props.isAddDisabled,
                                  }
                                )}
                              >
                                {props.isAddDisabled ? 'Adding...' : 'Add'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
